import { useQuery } from '@tanstack/react-query'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import apis from '../../../../modules/apis'
import { useAITutorPermission } from '../../../../modules/apis/student/ai-tutor'
import { useCustomerType } from '../../../../modules/apis/student/products'
import { axiosGet } from '../../../../modules/axiosHelper'
import { PRICING_CURRENCY_OPTIONS } from '../../../../modules/consts/pricing'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import { useLocaleVars } from '../../../../modules/i18n/useTranslation'
import { AI_TUTOR_COURSE, COUNTRY_ALPHA2_CODE } from '../../../../modules/vars'
import { usePromotionCode } from './PricingHero'

const ProductContext = createContext(null)
export const useProductContext = () => useContext(ProductContext)

export enum productTypeEnum {
  RINGLE_PACKAGE,
  PERSONAL_LESSON,
  AI_TUTOR,
}

const fetchB2CProducts = async (payload) => {
  const query = new URLSearchParams(payload).toString()
  const response = await axiosGet(`${apis.products.lines_b2b()}?${query}`)
  return response
}

const fetchB2B2CProducts = async (payload) => {
  const query = new URLSearchParams(payload).toString()
  const response = await axiosGet(`${apis.products.b2b2c()}?${query}`)
  return response
}

export const useFetchB2CProducts = (params) => {
  const payload = {
    product_line_type: params.productType,
    duration: params.duration,
    currency: params.currency || 'KRW',
    locale: params.locale || 'ko',
  }

  return useQuery({
    queryKey: ['b2c-products', params.productType, params.duration, params.currency || 'KRW', params.locale || 'ko'],
    queryFn: () => fetchB2CProducts(payload),
    enabled: !!params.productType,
  })
}

export const useFetchB2B2CProducts = (params) => {
  const payload = {
    duration: params.duration,
    currency: params.currency || 'KRW',
    locale: params.locale || 'ko',
  }

  return useQuery({
    queryKey: ['b2b2c-products', params.duration, params.currency || 'KRW', params.locale || 'ko'],
    queryFn: () => fetchB2B2CProducts(payload),
    enabled: !!params.duration,
  })
}

export const ProductProvider = ({ children }) => {
  const scrollTargetRef = useRef<HTMLButtonElement>(null)

  const router = useAppRouter()
  const { lang } = router.query

  const { duration_list } = useLocaleVars()
  const { currentUser } = useCurrentUser()
  const { data: customerTypeData } = useCustomerType({ enabled: !!currentUser })
  const { data: aiTutorMembershipPermissionData } = useAITutorPermission(AI_TUTOR_COURSE, {
    enabled: !!currentUser,
  })

  const [productType, setProductType] = useState({
    id: 24,
    type: productTypeEnum.RINGLE_PACKAGE,
  })
  const [purchaseInfo, setPurchaseInfo] = useState({
    id: null,
    duration: duration_list[0],
    countryCode: COUNTRY_ALPHA2_CODE.KR,
  })

  const convertedCountryCode =
    purchaseInfo?.countryCode == COUNTRY_ALPHA2_CODE.KR ? purchaseInfo.countryCode : COUNTRY_ALPHA2_CODE.US

  const productsPayload = {
    productType: productType.id,
    duration: purchaseInfo.duration.number,
    currency: PRICING_CURRENCY_OPTIONS.find((option) => option.country_code === purchaseInfo.countryCode)?.currency,
    locale: lang,
  }

  const hasAiTutorMembership =
    !!aiTutorMembershipPermissionData?.temp_membership || aiTutorMembershipPermissionData?.valid_memberships?.length > 0

  const scrollToTarget = () => {
    if (scrollTargetRef.current) {
      scrollTargetRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  useEffect(() => {
    if (!currentUser || !customerTypeData) return
  }, [customerTypeData])

  const { data: applicablePromotionId } = usePromotionCode()

  return (
    <ProductContext.Provider
      value={{
        scrollTargetRef,
        scrollToTarget,
        productsPayload,
        purchaseInfo,
        setPurchaseInfo,
        productType,
        setProductType,
        convertedCountryCode,
        applicablePromotionId,
        hasAiTutorMembership,
      }}
    >
      {children}
    </ProductContext.Provider>
  )
}
